import { lazy, StrictMode, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import 'ol/ol.css';
import './index.css';
// Módulos paralelos (focos, vetores, exportação): o import registra os codecs
// de URL deles. Precisa acontecer ANTES de ler a URL.
import './ui/modules';
// Codecs do app-core (c, z, g, t, q, v).
import './state/codecs';
import { readUrlState, startupSearch } from './state/urlSync';
import { installStaleBuildDetection } from './ui/UpdateNotice';
import { semZoomNaPagina } from './ui/semZoomNaPagina';
import App from './App.tsx';

/**
 * A página da denúncia cidadã (`/denuncia`, aberta pela ficha do foco numa aba
 * nova) é um documento, não o mapa: sem o estado da URL, sem a trava da pinça,
 * num pedaço separado do código que só baixa nela.
 */
// `/d/…` é o link curto da denúncia (denuncia/linkCurto.ts), que vai no texto enviado ao órgão.
const caminho = window.location.pathname.replace(/\/+$/, '');
const paginaDaDenuncia = caminho === '/denuncia' || caminho.startsWith('/d/');
const DenunciaPage = lazy(() => import('./denuncia/DenunciaPage'));
/** Termos de uso e privacidade (`/termos`): também um documento, carregado só nela. */
const paginaDosTermos = caminho === '/termos';
const TermosPage = lazy(() => import('./termos/TermosPage'));
/** Painel de acessos (`/painel`, só para o responsável, com senha): também à parte. */
const paginaDoPainel = caminho === '/painel';
const PainelPage = lazy(() => import('./painel/PainelPage'));
const pagina = paginaDaDenuncia || paginaDosTermos || paginaDoPainel;

if (!pagina) {
  // Link com parâmetros manda; sem eles, voltam as preferências salvas (urlSync.ts).
  readUrlState(startupSearch());
  semZoomNaPagina();
}
installStaleBuildDetection();

/**
 * Contagem anônima de acessos (Cloudflare Web Analytics, docs/painel-acessos.md):
 * sem cookie e sem identificar ninguém, só no site publicado. O painel em si
 * não se conta. O token é público (vai na página).
 */
const tokenDoContador = import.meta.env.VITE_CF_BEACON as string | undefined;
// Nem nas páginas da denúncia: o endereço delas (/d/…) leva o ponto e o horário do foco.
// Só no endereço público: um build de produção aberto na máquina (npm run preview) não conta.
if (import.meta.env.PROD && tokenDoContador && !paginaDoPainel && !paginaDaDenuncia && /(^|\.)amaview\.com\.br$/.test(location.hostname)) {
  const s = document.createElement('script');
  s.type = 'module';
  s.defer = true;
  s.src = 'https://static.cloudflareinsights.com/beacon.min.js';
  // spa: false — o app troca o endereço ao abrir painéis no celular; contar isso inflaria as páginas vistas.
  s.dataset.cfBeacon = JSON.stringify({ token: tokenDoContador, spa: false });
  document.head.appendChild(s);
}

if (import.meta.env.DEV || import.meta.env.VITE_DEBUG_HANDLE === '1') {
  // Acesso para depuração e para os scripts de verificação no navegador
  // (em builds de verificação: VITE_DEBUG_HANDLE=1 vite build).
  void import('./loop/dev/debugHandle').then((m) => m.installDebugHandle());
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    {pagina ? (
      <Suspense fallback={null}>{paginaDoPainel ? <PainelPage /> : paginaDosTermos ? <TermosPage /> : <DenunciaPage />}</Suspense>
    ) : (
      <App />
    )}
  </StrictMode>,
);
